import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Button,
  CardContent
} from '@mui/material';
import Slider from 'react-slick';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import i18next from 'i18next';
import { caseStudyContent } from '../../../data/caseStudyData';
import styles from './case-studies.module.scss';
import Images from '../../../assets/img';
import { useHistory } from 'react-router-dom';
import DeviceBreakPoints from '../../../deviceBreakPoints';

const CaseStudyNextArrow = ({ onClick, isSmallDevice, isMediumDevice, isTab }) => {
  let className = `${styles['testimonial-arrow']} ${styles['testimonial-next']}`;
  let backgroundColor = 'common.light';

  if (isSmallDevice) {
    className += ` ${styles['testimonial-next-mobile']}`;
  } else if (isMediumDevice) {
    className += ` ${styles['testimonial-next-small-device']}`;
  } else if (isTab) {
    className += ` ${styles['testimonial-next-tab']}`;
  }
  return (
    <Box
      backgroundColor={backgroundColor}
      className={className}
      sx={{ color: 'primary.main' }}
      onClick={onClick}
    >
      <ArrowForwardOutlinedIcon />
    </Box>
  );
};

const CaseStudyPrevArrow = ({ onClick, isSmallDevice, isMediumDevice, isTab }) => {
  let className = `${styles['testimonial-arrow']} ${styles['testimonial-prev']}`;
  let backgroundColor = 'common.light';

  if (isSmallDevice) {
    className += ` ${styles['testimonial-prev-mobile']}`;
  } else if (isMediumDevice) {
    className += ` ${styles['testimonial-prev-small-device']}`;
  } else if (isTab) {
    className += ` ${styles['testimonial-prev-tab']}`;
  }

  return (
    <Box
      backgroundColor={backgroundColor}
      className={className}
      sx={{ color: 'primary.main' }}
      onClick={onClick}
    >
      <ArrowBackOutlinedIcon />
    </Box>
  );
};

const CustomPaging = (index, currentSlide) => {
  return (
    <Box
      sx={{ color: index === currentSlide ? 'primary.main' : 'secondary.main' }}
      className={getClassName(index)}
    >
      &#9679;
    </Box>
  );
};

const getClassName = (index) => {
  if (index < caseStudyContent?.length) {
    if (index === currentSlide) return `${styles['dot-active']}`;
    else return `${styles['dot']}`;
  } else return `${styles['none']}`;
};

function CaseStudies() {
  const [imgIndex, setImgIndex] = useState(0);
  const [finalCaseStudyData, setFinalCaseStudyData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const maxSlidesToShow = 3;

  const history = useHistory();
  const { isMediumDevice, isSmallDevice, isTab } = DeviceBreakPoints();

  useEffect(() => {
    if (caseStudyContent.length > 0 && caseStudyContent.length <= maxSlidesToShow) {
      setFinalCaseStudyData(caseStudyContent.concat(caseStudyContent));
    } else {
      setFinalCaseStudyData(caseStudyContent);
    }
  }, []);

  const caseStudySettings = {
    infinite: true,
    dots: true,
    speed: 700,
    slidesToShow: 3,
    centerMode: true,
    centerPadding: '0px',
    nextArrow: (
      <CaseStudyNextArrow
        isSmallDevice={isSmallDevice}
        isMediumDevice={isMediumDevice}
        isTab={isTab}
      />
    ),
    prevArrow: (
      <CaseStudyPrevArrow
        isSmallDevice={isSmallDevice}
        isMediumDevice={isMediumDevice}
        isTab={isTab}
      />
    ),
    beforeChange: (current, next) => setImgIndex(next),
    afterChange: (index) => {
      if (index < caseStudyContent.length) {
        setCurrentSlide(index);
      } else {
        setCurrentSlide(index - caseStudyContent.length);
      }
    },
    customPaging: (index) => (
      <CustomPaging index={index} currentSlide={currentSlide} />
    )
  };

  const getImageStyle = () => {
    let imageStyle = `${styles['slide']} ${styles['slide-active']}`;
    if (isSmallDevice) {
      imageStyle += ` ${styles['slide-active-responsive']} ${styles['height-tab']}`;
    } else if (isTab) {
      imageStyle += ` ${styles['height-tab']}`;
    }
    return imageStyle;
  };

  const getElementStyle = () => {
    let elementStyle = `${styles['slide']}`;
    if (isSmallDevice) {
      elementStyle += ` ${styles['slide-responsive']} ${styles['height-tab']}`;
    } else if (isTab) {
      elementStyle += ` ${styles['height-tab']}`;
    }
    return elementStyle;
  };

  return (
    <>
      <Box py={3} sx={{ backgroundColor: 'primaryBackground.bgBody' }}></Box>
      <Box
        sx={{ backgroundColor: 'secondaryBackground.bgBody' }}
        className={
          isSmallDevice
            ? styles['container-padding-responsive']
            : styles['container-padding']
        }
      >
        <Typography mb={3} variant='h5' align='center'>
          {i18next.t('HOME_PAGE.CASE_STUDIES')}
        </Typography>
        <Container
          className={
            isSmallDevice
              ? styles['container-padding-responsive']
              : styles['container-padding']
          }
        >
          <Slider {...caseStudySettings}>
            {finalCaseStudyData.map((slide, index) => {
              return (
                <Card
                  sx={{ backgroundColor: 'primary.main' }}
                  variant='outlined'
                  square
                  className={
                    index === imgIndex ? getImageStyle() : getElementStyle()
                  }
                >
                  <CardContent className={styles['card-content']}>
                    <Grid container pb={3}>
                      <Grid
                        className={styles['grid-padding']}
                        item
                        xs={8}
                        sm={8}
                        md={8}
                        lg={7}
                        xl={7}
                      >
                        <Box p={0.5} mb={2} sx={{ height: 60 }}>
                          <Box
                            className={styles['card-icon']}
                            component='img'
                            sx={{ height: 55 }}
                            src={slide.icon}
                          />
                        </Box>
                        <Box className={styles['grid-content']}>
                          <Typography
                            variant='boldOnlyText'
                            color='common.light'
                            className={
                              isMediumDevice
                                ? styles['one-liner-responsive']
                                : styles['one-liner']
                            }
                          >
                            {i18next.t(slide.customerOneLiner)}
                          </Typography>
                          <Typography
                            color='common.light'
                            className={
                              isTab
                                ? styles['description-responsive']
                                : styles['description']
                            }
                          >
                            {i18next.t(slide.customerDescription)}
                          </Typography>
                          <Button
                            variant='whiteFilledBtn'
                            sx={{
                              fontSize: '13px',
                              color: 'common.dark !important'
                            }}
                            onClick={() => history.push(`/case-studies/${slide.id}`)}
                          >
                            {i18next.t('HOME_PAGE.CASE_STUDY_BUTTON')}
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={4} sm={4} md={4} lg={5} xl={5}>
                        <Box
                          component='img'
                          className={
                            isMediumDevice
                              ? styles['image-size-responsive']
                              : styles['image-size']
                          }
                          src={Images.caseStudyPage.caseStudyPageBannerImg}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
          </Slider>
        </Container>
      </Box>
      <Box py={3}></Box>
    </>
  );
}

export default CaseStudies;
